/**
 * Admin Customer Management — list + detail.
 * Backend: /api/admin/customers (list), /{id}, .../suspend, .../reactivate.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  Users, Search, MapPin, Calendar, ArrowLeft, Mail, Phone,
  XCircle, CheckCircle, Clock, FileText, Eye, Shield,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../lib/supabase';
import { formatDateNZ, formatRelativeNZ, formatDateTimeNZ } from '../lib/format';

const STATUS_COLORS = {
  active:        { bg: 'rgba(34,197,94,0.1)',  color: '#16A34A', label: 'Active' },
  suspended:     { bg: 'rgba(107,114,128,0.1)', color: '#6B7280', label: 'Suspended' },
  inactive_30d:  { bg: 'rgba(245,158,11,0.1)', color: '#D97706', label: 'Inactive 30d+' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || STATUS_COLORS.active;
  return (
    <span
      data-testid={`customer-status-${status}`}
      style={{ padding: '4px 10px', background: s.bg, color: s.color, borderRadius: '50px', fontSize: '0.75rem', fontWeight: 700 }}
    >
      {s.label}
    </span>
  );
};

const formatDate = formatDateNZ;
const formatRelative = formatRelativeNZ;

// ============================================
// ADMIN: CUSTOMERS LIST
// ============================================
export const AdminCustomersListPage = () => {
  const { session, isAuthenticated, loading: authLoading, profile } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const abortRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || profile?.role !== 'admin') { navigate('/login'); return; }
    fetchCustomers();
    // Cleanup any in-flight fetch when deps change (prevents 502 / race conditions)
    return () => { if (abortRef.current) abortRef.current.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, debouncedSearch, authLoading, isAuthenticated, profile]);

  // Debounce search input — 350 ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const fetchCustomers = async () => {
    // Abort any prior request so overlapping fetches don't 502
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (statusFilter) params.set('status', statusFilter);
      if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
      const res = await fetch(`${API_URL}/admin/customers?${params}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
        signal: ctrl.signal,
      });
      if (res.ok) {
        const data = await res.json();
        setCustomers(data.customers || []);
      } else {
        let body = null; try { body = await res.json(); } catch { /* noop */ }
        // eslint-disable-next-line no-console
        console.error('Failed to load customers:', body?.detail || res.status);
        setCustomers([]);
      }
    } catch (err) {
      if (err?.name === 'AbortError') return; // deliberate cancel
      // eslint-disable-next-line no-console
      console.error(err);
      setCustomers([]);
    }
    setLoading(false);
  };

  if (authLoading || loading) return <div className="loading">Loading customers...</div>;

  // Compute filter counts client-side from the unfiltered (status-filtered) set
  const total = customers.length;

  return (
    <div className="page admin-page" data-testid="admin-customers-page">
      <div className="page-header">
        <h1>Customer Management</h1>
        <p>Manage customer accounts and view their activity</p>
      </div>

      {/* Filter pills */}
      <div
        className="admin-stat-grid"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 8, marginBottom: 12 }}
        data-testid="customer-filter-pills"
      >
        {[
          { key: '',             label: 'All Customers', icon: <Users size={16} /> },
          { key: 'active',       label: 'Active',        icon: <CheckCircle size={16} /> },
          { key: 'inactive_30d', label: 'Inactive 30d+', icon: <Clock size={16} /> },
          { key: 'suspended',    label: 'Suspended',     icon: <XCircle size={16} /> },
        ].map(s => (
          <button
            key={s.key || 'all'}
            className={`admin-stat-btn ${statusFilter === s.key ? 'active' : ''}`}
            onClick={() => { setStatusFilter(s.key); }}
            data-testid={`customer-filter-${s.key || 'all'}`}
          >
            {s.icon}
            <span className="admin-stat-label">{s.label}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="admin-search-bar">
        <Search size={16} />
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          data-testid="customer-search"
        />
      </div>

      {/* Table */}
      <div className="admin-table-container" data-testid="customers-table">
        {customers.length === 0 ? (
          <div className="empty-state" style={{ padding: '48px 24px', textAlign: 'center' }}>
            <Users size={40} style={{ color: 'var(--text-muted)', marginBottom: '12px' }} />
            <p>No customers found</p>
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Location</th>
                <th style={{ textAlign: 'center' }}>Quote Requests</th>
                <th>Joined</th>
                <th>Last Login</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {customers.map(c => (
                <tr key={c.id} data-testid={`customer-row-${c.id}`}>
                  <td>
                    <div className="admin-cell-primary">{c.full_name || '—'}</div>
                    <div className="admin-cell-secondary">{c.email}</div>
                  </td>
                  <td><MapPin size={13} /> {c.city || '—'}{c.country_code ? `, ${c.country_code}` : ''}</td>
                  <td style={{ textAlign: 'center' }}>{c.quote_requests_count}</td>
                  <td>{formatDate(c.created_at)}</td>
                  <td title={c.last_sign_in_at || 'Never'}>{formatRelative(c.last_sign_in_at)}</td>
                  <td><StatusBadge status={c.status} /></td>
                  <td>
                    <Link
                      to={`/admin/customers/${c.id}`}
                      data-testid={`view-customer-${c.id}`}
                      style={{ padding: '6px 10px', background: 'var(--surface-hover)', border: '1px solid var(--border-color)', color: 'var(--text-dark)', borderRadius: 'var(--radius-sm)', textDecoration: 'none', fontSize: '0.75rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                    >
                      <Eye size={12} /> View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: 12, fontSize: '0.8125rem', color: 'var(--text-muted)' }} data-testid="customer-count">
        Showing {total} customer{total === 1 ? '' : 's'}
      </div>
    </div>
  );
};

// ============================================
// ADMIN: CUSTOMER DETAIL
// ============================================
export const AdminCustomerDetailPage = () => {
  const { id } = useParams();
  const { session, isAuthenticated, loading: authLoading, profile } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || profile?.role !== 'admin') { navigate('/login'); return; }
    fetchCustomer();
  }, [id, authLoading, isAuthenticated, profile]);

  const fetchCustomer = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin/customers/${id}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) {
        setData(await res.json());
      } else {
        let body = null; try { body = await res.json(); } catch {}
        setActionMsg({ type: 'error', text: body?.detail || `Failed to load customer (HTTP ${res.status})` });
      }
    } catch (err) {
      setActionMsg({ type: 'error', text: `Network error: ${err?.message || 'unknown'}` });
    }
    setLoading(false);
  };

  const performAction = async (action) => {
    const confirmMsg = action === 'suspend'
      ? `Suspend ${data?.full_name || 'this customer'}? They won't be able to log in until reactivated. Their profile and quote history will be preserved.`
      : `Reactivate ${data?.full_name || 'this customer'}? They'll regain login access immediately.`;
    if (!window.confirm(confirmMsg)) return;
    setActionLoading(true);
    setActionMsg(null);
    try {
      const res = await fetch(`${API_URL}/admin/customers/${id}/${action}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      let body = null; try { body = await res.json(); } catch {}
      if (res.ok) {
        setActionMsg({ type: 'success', text: body?.message || 'Done.' });
        fetchCustomer();
      } else {
        setActionMsg({ type: 'error', text: body?.detail || `Action failed (HTTP ${res.status})` });
      }
    } catch (err) {
      setActionMsg({ type: 'error', text: `Network error: ${err?.message || 'unknown'}` });
    }
    setActionLoading(false);
  };

  if (authLoading || loading) return <div className="loading">Loading customer...</div>;
  if (!data) return (
    <div className="page admin-page" data-testid="customer-detail-error">
      <Link to="/admin/customers" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)', textDecoration: 'none', fontSize: '0.875rem' }}>
        <ArrowLeft size={14} /> Back to customers
      </Link>
      <div className="empty-state" style={{ marginTop: 20 }}><p>Customer not found.</p></div>
    </div>
  );

  return (
    <div className="page admin-page" data-testid="admin-customer-detail">
      <Link to="/admin/customers" data-testid="back-to-customers" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)', textDecoration: 'none', fontSize: '0.875rem', marginBottom: 16 }}>
        <ArrowLeft size={14} /> Back to customers
      </Link>

      {actionMsg && (
        <div
          data-testid="customer-action-msg"
          style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 'var(--radius-sm)', background: actionMsg.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(220,38,38,0.1)', color: actionMsg.type === 'success' ? '#16A34A' : '#DC2626', fontSize: '0.875rem', fontWeight: 600 }}
        >
          {actionMsg.text}
        </div>
      )}

      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {data.full_name || 'Unnamed Customer'}
            {data.is_suspended ? <StatusBadge status="suspended" /> : <StatusBadge status="active" />}
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            <Mail size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} />
            {data.email}
            {data.phone && <>
              <span style={{ margin: '0 8px' }}>·</span>
              <Phone size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} />
              {data.phone}
            </>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {data.is_suspended ? (
            <button
              onClick={() => performAction('reactivate')}
              disabled={actionLoading}
              data-testid="reactivate-customer-btn"
              style={{ padding: '10px 16px', background: 'var(--accent)', border: '1px solid var(--accent)', color: '#fff', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <CheckCircle size={14} /> {actionLoading ? 'Working...' : 'Reactivate'}
            </button>
          ) : (
            <button
              onClick={() => performAction('suspend')}
              disabled={actionLoading}
              data-testid="suspend-customer-btn"
              style={{ padding: '10px 16px', background: '#fff', border: '1px solid #DC2626', color: '#DC2626', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <XCircle size={14} /> {actionLoading ? 'Working...' : 'Suspend Account'}
            </button>
          )}
        </div>
      </div>

      {/* Top stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Quote Requests', value: data.quote_requests_count, icon: <FileText size={16} /> },
          { label: 'Joined', value: formatDate(data.created_at), icon: <Calendar size={16} /> },
          { label: 'Last Login', value: formatRelative(data.last_sign_in_at), icon: <Clock size={16} />, sub: data.last_sign_in_at ? formatDateTimeNZ(data.last_sign_in_at) : 'Never logged in' },
          { label: 'Location', value: data.city || '—', icon: <MapPin size={16} />, sub: data.country_code || '' },
        ].map((c, i) => (
          <div key={i} style={{ background: '#fff', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '14px 16px' }} data-testid={`customer-stat-${i}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)', fontSize: '0.75rem', marginBottom: 4 }}>
              {c.icon} <span>{c.label}</span>
            </div>
            <div style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--text-dark)' }}>{c.value}</div>
            {c.sub && <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: 2 }}>{c.sub}</div>}
          </div>
        ))}
      </div>

      {/* Recent quote requests */}
      <div style={{ background: '#fff', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '18px 20px' }}>
        <h2 style={{ fontSize: '0.9375rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
          <FileText size={16} /> Recent Quote Requests ({data.recent_quotes?.length || 0})
        </h2>
        {(!data.recent_quotes || data.recent_quotes.length === 0) ? (
          <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>This customer hasn't submitted any quote requests yet.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table" data-testid="customer-quotes-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_quotes.map(q => (
                  <tr key={q.id} data-testid={`customer-quote-row-${q.id}`}>
                    <td>
                      <div className="admin-cell-primary">{q.service_name}</div>
                      <div className="admin-cell-secondary" style={{ maxWidth: 360 }}>{q.job_description || '—'}</div>
                    </td>
                    <td>{q.city || '—'}{q.country_code ? `, ${q.country_code}` : ''}</td>
                    <td><span style={{ padding: '2px 8px', background: 'var(--surface-hover)', borderRadius: '50px', fontSize: '0.6875rem', fontWeight: 600 }}>{q.status || 'open'}</span></td>
                    <td>{formatDate(q.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer audit info */}
      <div style={{ marginTop: 18, padding: '10px 14px', background: 'var(--surface-hover)', borderRadius: 'var(--radius-sm)', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
        <Shield size={11} style={{ verticalAlign: '-1px', marginRight: 4 }} />
        Admin actions on this customer (suspend / reactivate) are logged to <code>admin_actions</code> for audit.
      </div>
    </div>
  );
};
